import { dispatch } from '@omni/kit/ActionHandler';
import { Optional } from '@omni/kit/Types';
import {
  KitButton,
  KitIcon,
  KitInput,
  KitListItem,
  KitSnack,
  KitText,
  KitTouchable,
} from '@omni/kit/components';
import KitAvatar from '@omni/kit/components/KitAvatar';
import KitEditableAvatar from '@omni/kit/components/KitEditableAvatar';
import { IconName } from '@omni/kit/components/KitIcon';
import { KitSnackDuration } from '@omni/kit/components/KitSnack.types';
import Show from '@omni/kit/components/Show';
import {
  SUBSPLASH_AUTH_PROVIDER_ID,
  SUBSPLASH_PHONE_AUTH_PROVIDER_ID,
} from '@omni/kit/constants/identifiers';
import { useScreenContext } from '@omni/kit/contexts/ScreenContext';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import useShellAuthProviders from '@omni/kit/contexts/ShellContext/hooks/useShellAuthProviders';
import { WindowSizeMin } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import BaseService from '@omni/kit/services/BaseService';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { notifyAppUserSettingsUpdated } from '@omni/kit/utilities/NativeHelpers';
import formatPhoneNumberByRegion from '@omni/kit/utilities/formatPhoneNumberByRegion';
import {
  capitalizeFirstLetter,
  handleHttpErrorResponse,
} from '@omni/kit/utilities/utilities';
import {
  setProfileImageUrl,
  universalLogout,
} from '@omni/messaging/shared/redux/actions/SystemActions';
import moment from 'moment';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Keyboard, Platform, StyleSheet, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useDispatch } from 'react-redux';

import { useBlockPageContext } from '../../BlockPageContext';
import { IBlockProps, SettingInterface, UpdateActionInterface } from '../types';
import ExtraFields from './ExtraFields';
import AuthProvider from './authProvider/AuthProvider';
import ChangeEmailModal from './modals/ChangeEmailModal';
import ChangePasswordModal from './modals/ChangePasswordModal';
import ChangePhoneNumberModal from './modals/ChangePhoneNumberModal';
import DeleteAccountModal from './modals/DeleteAccountModal';
import RemovePhoneLoginModal from './modals/RemovePhoneLoginModal';
import { IUserProfile } from './types';
import {
  calculateProgressNumber,
  checkBirthdayValidation,
  checkPhoneValidation,
  formatPhoneNumber,
  isAccountEdited,
  isValidAddress,
  sanitizePhoneNumber,
} from './utilities';

const debug = require('debug')('tca:blocks:blocks:AccountBlock');

export interface IAccountBlockProps extends IBlockProps, IUserProfile {
  /** Action to perform when a user delete their profile. */
  deleteAccountAction?: UpdateActionInterface;

  /**
   * Action to perform when a user changes account info such as first and last
   * name.
   */
  updateAccountAction?: UpdateActionInterface;

  /**
   * Action to perform when a user changes account info such when it is a fluro
   * user.
   */
  updateProfileAction?: UpdateActionInterface;

  /** Action to perform when a user changes their email address. */
  updateEmailAction?: UpdateActionInterface;

  /** Action to perform when a user changes their phone number. */
  updatePhoneAction?: UpdateActionInterface;

  /** Action to perform when a user changes their password. */
  updatePasswordAction?: UpdateActionInterface;

  loggedInAuthProvider?: {
    id: string;
    title?: string;
    icon?: IconName;
    color?: string;
  };
}

/** These additional props are added by resolver.tsx and are not part of the API feed for the account block */
export interface AccountBlockComponentProps extends IAccountBlockProps {
  addressRequired?: boolean;
  appKey: string;
  clientBrandColor?: string;
  includeLogoutButton?: boolean;
  isLoading?: boolean;
  largeSaveButtonEnabled?: boolean;
  onDismiss?: () => void;
  submitActionRef?: React.MutableRefObject<() => void>;
  setIsEdited?: (isEdited: boolean) => void;
  setIsLoading?: (isLoading: boolean) => void;
  setRefreshing?: (refreshing: boolean) => void;
}

/**
 * Changelog:
 * - 5.15.0: Support for editable profile photo and new fields such as birthday, phone number and location
 * - 5.11.0: AccountBlock added. Support for editable name and email.
 */
const AccountBlock: React.FC<AccountBlockComponentProps> = ({
  // Data
  accountDeleted = false,
  addressLine1 = '',
  addressLine2 = '',
  addressRequired = false,
  appKey,
  appTitle = '',
  city = '',
  clientBrandColor,
  country = '',
  dateOfBirth = '',
  email,
  firstName,
  horizontalSpacing,
  id,
  image = '',
  includeLogoutButton = false,
  isLoading = false,
  largeSaveButtonEnabled = false,
  lastName,
  loggedInAuthProvider,
  phoneNumber = '',
  phoneNumberObj,
  verifiedPhoneNumber,
  postalCode = '',
  profileID,
  state = '',

  // Actions
  onDismiss,
  setIsEdited,
  setIsLoading,
  setRefreshing,
  submitActionRef,
  deleteAccountAction,
  updateAccountAction,
  updateEmailAction,
  updatePasswordAction,
  updateProfileAction,
  updatePhoneAction,
}): ReactElement => {
  const { t } = useTranslation(['account']);

  // ---------------------------------------------------------------------------
  // Global State
  const { tokens, dispatchEvent } = useShellContext();
  const { targetProviders, loginEnabledProviders } = useShellAuthProviders();
  const { authProviderId } = useBlockPageContext();
  const reduxDispatch = useDispatch();

  const targetProviderToken = useMemo(
    () =>
      authProviderId
        ? tokens.accessTokens[authProviderId] || undefined
        : undefined,
    [authProviderId, tokens.accessTokens]
  );

  const hasPhoneAuthProviderEnabled = useMemo(() => {
    return loginEnabledProviders.some(
      (provider) => provider.authproviderid === SUBSPLASH_PHONE_AUTH_PROVIDER_ID
    );
  }, [loginEnabledProviders]);

  // ---------------------------------------------------------------------------
  // Local State
  const [_snackOptions, _setSnackOptions] = useState({
    duration: KitSnackDuration.SHORT,
    marginBottom: Spacing.l,
    marginTop: '95vh',
    message: '',
    visible: false,
  });

  const [addressLine1Value, setAddressLine1Value] = useState('');
  const [addressLine2Value, setAddressLine2Value] = useState('');
  const [cityValue, setCityValue] = useState('');
  const [countryValue, setCountryValue] = useState<string | number>(country);
  const [dateOfBirthValue, setDateOfBirthValue] = useState('');
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [phoneNumberModalVisible, setPhoneNumberModalVisible] = useState(false);
  const [firstNameValue, setFirstNameValue] = useState('');
  const [lastNameValue, setLastNameValue] = useState('');
  const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(true);
  const [isLastNameValid, setIsLastNameValid] = useState<boolean>(true);
  const [isNameValid, setIsNameValid] = useState<boolean>(true);
  const [deleteAccountModalVisible, setDeleteAccountModalVisible] =
    useState<boolean>(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [phoneNumberValue, setPhoneNumberValue] = useState('');
  const [postalCodeValue, setPostalCodeValue] = useState('');
  const [profilePhotoId, setProfilePhotoId] = useState('');
  const [profilePhotoUrlValue, setProfilePhotoUrlValue] = useState('');
  const [stateValue, setStateValue] = useState<string | number>('');
  const [removePhoneLoginModalVisible, setRemovePhoneLoginModalVisible] =
    useState<boolean>(false);

  // ---------------------------------------------------------------------------
  // Computed State

  const shouldShowAuthProvider = useMemo((): boolean => {
    if (
      loggedInAuthProvider?.id === SUBSPLASH_AUTH_PROVIDER_ID ||
      loggedInAuthProvider?.id === SUBSPLASH_PHONE_AUTH_PROVIDER_ID
    ) {
      return false;
    }

    return Boolean(
      ((!updateAccountAction && !updateProfileAction) ||
        !updateEmailAction ||
        !updatePasswordAction) &&
        loggedInAuthProvider?.title &&
        loggedInAuthProvider?.icon
    );
  }, [
    updateAccountAction,
    updateProfileAction,
    updateEmailAction,
    updatePasswordAction,
    loggedInAuthProvider?.id,
    loggedInAuthProvider?.title,
    loggedInAuthProvider?.icon,
  ]);

  const progressActivityNumber = useMemo((): number => {
    // calculate percentage how many fields are completed
    return calculateProgressNumber({
      addressLine1,
      city,
      country,
      dateOfBirth,
      phoneNumber,
      postalCode,
      state,
      hasPhoneAuthProviderEnabled,
    });
  }, [
    addressLine1,
    city,
    country,
    dateOfBirth,
    phoneNumber,
    postalCode,
    state,
    hasPhoneAuthProviderEnabled,
  ]);

  const isEdited = useMemo((): boolean => {
    return isAccountEdited({
      addressLine1,
      addressLine1Value,
      addressLine2,
      addressLine2Value,
      city,
      cityValue,
      country,
      countryValue,
      dateOfBirth,
      dateOfBirthValue,
      firstName,
      firstNameValue,
      hasExtraFields: Boolean(updateProfileAction),
      image,
      lastName,
      lastNameValue,
      phoneNumber,
      phoneNumberValue,
      postalCode,
      postalCodeValue,
      profilePhotoUrlValue,
      state,
      stateValue,
    });
  }, [
    addressLine1,
    addressLine1Value,
    addressLine2,
    addressLine2Value,
    city,
    cityValue,
    country,
    countryValue,
    dateOfBirth,
    dateOfBirthValue,
    firstName,
    firstNameValue,
    image,
    lastName,
    lastNameValue,
    phoneNumber,
    phoneNumberValue,
    postalCode,
    postalCodeValue,
    profilePhotoUrlValue,
    state,
    stateValue,
    updateProfileAction,
  ]);

  const hasMissingAddressFields = useMemo((): boolean => {
    return (
      addressRequired &&
      !isValidAddress(
        countryValue,
        addressLine1Value,
        cityValue,
        stateValue,
        postalCodeValue
      )
    );
  }, [
    addressLine1Value,
    addressRequired,
    cityValue,
    countryValue,
    postalCodeValue,
    stateValue,
  ]);

  const phoneNumberDisplayFormatted = useMemo((): string => {
    return phoneNumberObj
      ? `${phoneNumberObj?.country?.calling_code} ${formatPhoneNumberByRegion(
          phoneNumberObj?.significant,
          phoneNumberObj?.country?.region_code
        )}`
      : phoneNumber;
  }, [phoneNumberObj, phoneNumber]);

  // ---------------------------------------------------------------------------
  // Callbakcs

  const handleNamesErrorMessage = useCallback(
    (errorMessage: Optional<string> = null, error: Optional<string> = null) => {
      if (error === 'first_name_cannot_contain_emojis') {
        setIsFirstNameValid(false);
      } else if (error === 'last_name_cannot_contain_emojis') {
        setIsLastNameValid(false);
      } else if (error === 'names_cannot_contain_emojis') {
        setIsNameValid(false);
      } else {
        const snackMessage = errorMessage?.length
          ? capitalizeFirstLetter(errorMessage)
          : t('account:saveError');

        KitSnack.show(snackMessage, KitSnack.duration.SHORT);
      }
    },
    [t]
  );

  const handleNameUpdated = useCallback(
    (
      success = true,
      data: Optional<{ error: string }> = null,
      errorMessage: Optional<string> = null
    ): void => {
      setIsFirstNameValid(true);
      setIsLastNameValid(true);
      setIsNameValid(true);
      const error = data?.error;

      if (success) {
        KitSnack.show(t('account:accountUpdated'));
        // Server takes a while before returning the updated data
        setTimeout(() => {
          if (Platform.OS === 'web') {
            // This triggers a reload of the account feed
            setRefreshing?.(true);
          }

          notifyAppUserSettingsUpdated();
        }, 500);
      } else {
        // Show save button if any error occurs
        setIsEdited?.(true);
        handleNamesErrorMessage(errorMessage, error);
      }

      setIsLoading?.(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const saveNewPassword = async (
    oldPassword: string,
    newPassword: string
  ): Promise<void> => {
    const originalAction = updatePasswordAction;

    if (originalAction?.updateUrl && originalAction?.settings?.[0]) {
      Keyboard.dismiss();

      const settings = [...(originalAction?.settings ?? [{}])];
      settings[0] = { ...settings[0], value: [oldPassword, newPassword] };

      const url = originalAction.updateUrl;

      const res = await BaseService.Put({
        url,
        appKey,
        authProviderId,
        targetProviders,
        token: targetProviderToken,
        data: { profileID, settings },
        headers: {
          'Content-Type': 'application/json',
        },
      });

      notifyAppUserSettingsUpdated();

      const ok = res.status === 201 || res.status === 200;

      if (!ok) {
        throw new Error();
      }
    }
  };

  const handleLogOut = (): void => {
    if (onDismiss) {
      onDismiss();
    }

    // logout for the web-app, relevant when a user chooses to delete their account
    // or removes their trusted phone number when logged-in with that phone number
    dispatchEvent({
      type: 'DEAUTHENTICATE_USER',
    });

    // DISP-4928: Dispatch logout after a delay to prevent the app from
    // freezing, crashing or the auth modal being rendered unexpectedly
    // after returning to PAM on the 2nd+ login attempt
    setTimeout(() => {
      reduxDispatch(universalLogout());
    }, 500);
  };

  const handleSave = (): void => {
    if (addressRequired && hasMissingAddressFields) {
      KitSnack.show(t('account:requiredFields'));

      return;
    }

    submitActionRef?.current?.();

    if (Platform.OS === 'web') {
      // prevent usage of web specific window and CustomEvent objects on iOS & Android
      window.dispatchEvent(
        new CustomEvent('account-saved', {
          bubbles: true,
          cancelable: false,
          composed: true,
        })
      );
    }
  };

  const saveNewName = useCallback(async (): Promise<void> => {
    if ((updateAccountAction?.settings || []).length > 1) {
      setIsLoading?.(true);
      setIsEdited?.(false);
      const settings = [...(updateAccountAction?.settings ?? [{}])];

      for (let i = 0; i < settings.length; i++) {
        if (settings[i].sapid === 'app_user_first_name') {
          settings[i] = { ...settings[i], value: firstNameValue };
        } else if (settings[i].sapid === 'app_user_last_name') {
          settings[i] = { ...settings[i], value: lastNameValue };
        }
      }

      const url = updateAccountAction?.updateUrl;

      const res = await BaseService.Put({
        url: url as string,
        appKey,
        authProviderId,
        targetProviders,
        token: targetProviderToken,
        data: { settings },
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (res.status === 201 || res.status === 200) {
        handleNameUpdated();
      } else {
        const { data, message } = await handleHttpErrorResponse(res);
        handleNameUpdated(false, data, message);
      }

      Keyboard.dismiss();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appKey,
    firstNameValue,
    handleNameUpdated,
    lastNameValue,
    setIsEdited,
    setIsLoading,
    updateAccountAction,
  ]);

  const saveAccountDataFields = useCallback(async (): Promise<void> => {
    if (
      (updateProfileAction?.settings || []).length > 1 &&
      checkBirthdayValidation(dateOfBirthValue) &&
      (hasPhoneAuthProviderEnabled || checkPhoneValidation(phoneNumberValue))
    ) {
      setIsLoading?.(true);
      setIsEdited?.(false);
      const settings: Array<SettingInterface | null> = [
        ...(updateProfileAction?.settings ?? [{}]),
      ];

      for (let i = 0; i < settings.length; i++) {
        if (settings[i]?.sapid === 'app_user_first_name') {
          settings[i] = { ...settings[i], value: firstNameValue };
        } else if (settings[i]?.sapid === 'app_user_last_name') {
          settings[i] = { ...settings[i], value: lastNameValue };
        } else if (settings[i]?.sapid === 'app_user_profile_photo_id') {
          if (profilePhotoUrlValue !== image) {
            settings[i] = {
              ...settings[i],
              value: profilePhotoId,
            };
          } else {
            // Set app_user_profile_photo_id as null if photo is not updated.
            settings[i] = null;
          }
        } else if (settings[i]?.sapid === 'app_user_date_of_birth') {
          settings[i] = {
            ...settings[i],
            value: dateOfBirthValue
              ? moment(dateOfBirthValue, 'MM/DD/YYYY').format()
              : '',
          };
        } else if (
          settings[i]?.sapid === 'app_user_phone_number' &&
          !hasPhoneAuthProviderEnabled
        ) {
          settings[i] = {
            ...settings[i],
            value: phoneNumberValue
              ? sanitizePhoneNumber(phoneNumberValue)
              : '',
          };
        } else if (settings[i]?.sapid === 'app_user_address_line_1') {
          settings[i] = { ...settings[i], value: addressLine1Value };
        } else if (settings[i]?.sapid === 'app_user_address_line_2') {
          settings[i] = { ...settings[i], value: addressLine2Value };
        } else if (settings[i]?.sapid === 'app_user_city') {
          settings[i] = { ...settings[i], value: cityValue };
        } else if (settings[i]?.sapid === 'app_user_state') {
          settings[i] = { ...settings[i], value: stateValue };
        } else if (settings[i]?.sapid === 'app_user_country') {
          settings[i] = { ...settings[i], value: countryValue };
        } else if (settings[i]?.sapid === 'app_user_postal_code') {
          settings[i] = { ...settings[i], value: postalCodeValue };
        }
      }

      /**
       * In case profile image is not updated, we should remove null that is set
       * above and send the payload without image object.
       */
      const settingsToUpdate = settings.filter(Boolean) as SettingInterface[];

      const url = updateProfileAction?.updateUrl;

      const res = await BaseService.Put({
        url: url as string,
        appKey,
        authProviderId,
        targetProviders,
        token: targetProviderToken,
        data: { profileID, settings: settingsToUpdate },
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const ok = res.status === 201 || res.status === 200;

      if (ok) {
        handleNameUpdated();
        if (profilePhotoUrlValue !== image) {
          reduxDispatch(setProfileImageUrl(profilePhotoUrlValue));
        }
      } else {
        const { data, message } = await handleHttpErrorResponse(res);
        handleNameUpdated(false, data, message);
      }

      Keyboard.dismiss();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addressLine1Value,
    addressLine2Value,
    appKey,
    cityValue,
    countryValue,
    dateOfBirthValue,
    firstNameValue,
    handleNameUpdated,
    image,
    lastNameValue,
    phoneNumberValue,
    postalCodeValue,
    profileID,
    profilePhotoId,
    profilePhotoUrlValue,
    setIsEdited,
    setIsLoading,
    stateValue,
    updateProfileAction?.settings,
    updateProfileAction?.updateUrl,
  ]);
  // ---------------------------------------------------------------------------
  // Effects

  useEffect((): void => {
    /**
     * The initial state may have changed while the component is mounted
     * (e.g. during cache refresh).
     */
    setAddressLine1Value(addressLine1);
    setAddressLine2Value(addressLine2);
    setCityValue(city);
    setCountryValue(country);
    setDateOfBirthValue(
      dateOfBirth ? moment(dateOfBirth).format('MM/DD/YYYY') : ''
    );
    setFirstNameValue(firstName);
    setLastNameValue(lastName);
    setPhoneNumberValue(
      hasPhoneAuthProviderEnabled
        ? phoneNumberDisplayFormatted
        : formatPhoneNumber(phoneNumber)
    );
    setPostalCodeValue(postalCode);
    setProfilePhotoUrlValue(image);
    setStateValue(state);
  }, [
    addressLine1,
    addressLine2,
    city,
    country,
    dateOfBirth,
    firstName,
    image,
    lastName,
    phoneNumber,
    phoneNumberDisplayFormatted,
    postalCode,
    state,
    hasPhoneAuthProviderEnabled,
  ]);

  useEffect((): void => {
    setIsEdited?.(isEdited);

    // remove names error message when account is not edited
    if (!isEdited) {
      setIsFirstNameValid(true);
      setIsLastNameValid(true);
      setIsNameValid(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdited]);

  useEffect((): void => {
    // Logout user from device if the acccount is deleted
    if (accountDeleted) {
      handleLogOut();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountDeleted]);

  useEffect((): void => {
    if (submitActionRef) {
      submitActionRef.current = updateProfileAction
        ? saveAccountDataFields
        : saveNewName;
    }
  }, [
    saveAccountDataFields,
    saveNewName,
    submitActionRef,
    updateProfileAction,
  ]);

  const { edgeSpacing, viewPortWidth } = useScreenContext({
    fixedSpacingType: horizontalSpacing,
  });
  const layout2Column = viewPortWidth >= WindowSizeMin.Medium;

  return (
    <View testID='AccountBlock'>
      <KeyboardAwareScrollView
        keyboardShouldPersistTaps='always'
        enableOnAndroid={true}
        extraScrollHeight={Platform.OS === 'android' ? 100 : 0}
        testID='KeyboardAwareScrollView'
      >
        <View
          style={{ marginHorizontal: edgeSpacing, paddingBottom: Spacing.xxl }}
          testID='AccountBlockContainer'
        >
          <Show show={!isFirstNameValid || !isLastNameValid || !isNameValid}>
            <View
              testID='ValidationContainer'
              style={[
                styles.errorLabel,
                !isLastNameValid ? { right: 0 } : { left: 0 },
              ]}
            >
              <KitText
                fontSize={14}
                bold
                style={{ color: Colors.N0 }}
                testID='NameErrorMessage'
              >
                {t('account:emojisError')}
              </KitText>
            </View>
          </Show>
          <View style={styles.headerContainer} testID='AvatarContainer'>
            <Show show={Boolean(updateAccountAction)}>
              <KitAvatar
                border={false}
                nickname={firstNameValue + ' ' + lastNameValue || ''}
                size={104}
                testID='Avatar'
              />
            </Show>
            <Show show={Boolean(updateProfileAction)}>
              <KitEditableAvatar
                appKey={appKey}
                endUserId={id}
                imageUrl={profilePhotoUrlValue}
                nickname={firstNameValue + ' ' + lastNameValue || ''}
                onPhotoChange={(imageValue: { id: string; url: string }) => {
                  setProfilePhotoId(imageValue.id);
                  setProfilePhotoUrlValue(imageValue.url);
                }}
              />
            </Show>
            <KitText
              h1
              style={{ marginTop: Spacing.s }}
              testID='FistAndLastName'
            >
              {firstNameValue} {lastNameValue}
            </KitText>
          </View>
          <Show show={Boolean(updateAccountAction || updateProfileAction)}>
            <View
              style={{
                flexDirection: 'row',
                marginBottom: Spacing.l,
                marginTop: Spacing.m,
              }}
              testID='UserNameContainer'
            >
              <KitInput
                label={t('account:inputLabelFirst')}
                onChangeText={setFirstNameValue}
                style={{
                  flex: 1,
                  marginRight: Spacing.l,
                }}
                testID='FirstName'
                value={firstNameValue}
                isValid={isFirstNameValid ? isNameValid : undefined}
              />
              <KitInput
                label={t('account:inputLabelLast')}
                onChangeText={setLastNameValue}
                style={{ flex: 1 }}
                testID='LastName'
                value={lastNameValue}
                isValid={isLastNameValid ? isNameValid : undefined}
              />
            </View>
          </Show>
          <Show show={Boolean(updateEmailAction)}>
            <View style={{ marginBottom: Spacing.l }} testID='EmailContainer'>
              <KitText semiBold style={styles.label} testID='Label Email'>
                {t('account:emailAddress')}
              </KitText>
              <KitTouchable
                testID='EmailAddressTouchable'
                noRadius
                onPress={() => {
                  if (
                    updateEmailAction?.handler === 'appUser' &&
                    updateEmailAction?.updateUrl
                  ) {
                    setEmailModalVisible(true);
                  } else {
                    dispatch(updateEmailAction);
                  }
                }}
              >
                <KitInput
                  editable={false}
                  outline
                  rightIcon={<KitIcon name='edit' size={20} />}
                  testID='EmailAccount'
                  value={email}
                />
              </KitTouchable>
            </View>
          </Show>
          {updatePhoneAction && hasPhoneAuthProviderEnabled ? (
            <>
              <View style={{ marginBottom: Spacing.l }} testID='PhoneContainer'>
                <KitText
                  semiBold
                  style={styles.label}
                  testID='Label Phone Number'
                >
                  {t('account:mobilePhoneNumber')}
                </KitText>
                <KitTouchable
                  testID='PhoneNumberTouchable'
                  noRadius
                  onPress={() => {
                    if (updatePhoneAction?.updateUrl) {
                      setPhoneNumberModalVisible(true);
                    } else {
                      dispatch(updatePhoneAction);
                    }
                  }}
                >
                  <KitInput
                    editable={false}
                    outline
                    rightIcon={<KitIcon name='edit' size={20} />}
                    rightIconStyle={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      width: undefined,
                      gap: Spacing.m,
                    }}
                    testID='PhoneNumberAccount'
                    value={phoneNumberValue}
                  />
                </KitTouchable>
              </View>
              {(updatePhoneAction?.loginWithPhoneSupported &&
                updatePhoneAction?.phoneVerifiable) ||
              verifiedPhoneNumber?.significant ? (
                <KitListItem
                  title={
                    verifiedPhoneNumber?.significant
                      ? t('account:phoneVerifiedForLogin')
                      : t('account:verifyYourPhoneForLogin')
                  }
                  subtitle={
                    verifiedPhoneNumber?.significant
                      ? `${
                          verifiedPhoneNumber?.country?.calling_code
                        } ${formatPhoneNumberByRegion(
                          verifiedPhoneNumber?.significant,
                          verifiedPhoneNumber?.country?.region_code
                        )}`
                      : t('account:verifyYourPhoneForLoginSubtitle')
                  }
                  fullBorder
                  fullBorderStyle={{ borderRadius: Spacing.s }}
                  innerPaddingHorizontal={Spacing.l}
                  innerPaddingVertical={Spacing.l}
                  style={{ marginBottom: Spacing.l, borderRadius: Spacing.s }}
                  ImageComponent={
                    verifiedPhoneNumber?.significant ? (
                      <KitIcon
                        name='lock'
                        size={20}
                        color={Colors.N1000}
                        style={{ marginRight: 6 }}
                      />
                    ) : (
                      <KitIcon
                        name='lock-open'
                        size={20}
                        color={Colors.N1000}
                        style={{ marginRight: 6 }}
                      />
                    )
                  }
                  onPress={
                    !verifiedPhoneNumber?.significant &&
                    ((!phoneNumber && !phoneNumberObj?.significant) ||
                      (updatePhoneAction?.loginWithPhoneSupported &&
                        updatePhoneAction?.phoneVerifiable))
                      ? () => {
                          if (updatePhoneAction?.updateUrl) {
                            setPhoneNumberModalVisible(true);
                          } else {
                            dispatch(updatePhoneAction);
                          }
                        }
                      : undefined
                  }
                  rightIcon={
                    !verifiedPhoneNumber?.significant &&
                    ((!phoneNumber && !phoneNumberObj?.significant) ||
                      (updatePhoneAction?.loginWithPhoneSupported &&
                        updatePhoneAction?.phoneVerifiable))
                      ? 'arrow-right'
                      : ''
                  }
                  showRightIconOnAndroid
                  onRightActionPress={() => {
                    // User can delete phone authenticator only if they have an email/password login method
                    if (updatePasswordAction) {
                      setRemovePhoneLoginModalVisible(true);
                    }
                  }}
                  rightActionIcon={
                    verifiedPhoneNumber?.significant &&
                    // User can delete phone authenticator only if they have an email/password login method
                    Boolean(updatePasswordAction)
                      ? 'delete'
                      : ''
                  }
                  iconColor={Colors.N300}
                />
              ) : null}
            </>
          ) : null}
          <Show show={Boolean(updatePasswordAction)}>
            <View
              style={{ marginBottom: Spacing.l }}
              testID='PasswordContainer'
            >
              <KitText semiBold style={styles.label} testID='LabelPassword'>
                {t('account:password')}
              </KitText>
              <KitTouchable
                testID='PasswordTouchable'
                noRadius
                onPress={() => {
                  if (
                    updatePasswordAction?.handler === 'appUser' &&
                    updatePasswordAction?.updateUrl
                  ) {
                    setPasswordModalVisible(true);
                  } else {
                    dispatch(updatePasswordAction);
                  }
                }}
              >
                <KitInput
                  editable={false}
                  outline
                  testID='PasswordAccount'
                  rightIcon={<KitIcon name='edit' size={20} />}
                  value='••••••••••'
                />
              </KitTouchable>
            </View>
          </Show>

          {shouldShowAuthProvider ? (
            <AuthProvider
              email={email}
              loggedInAuthProvider={loggedInAuthProvider}
            />
          ) : null}

          <Show show={Boolean(updateProfileAction)}>
            <ExtraFields
              addressLine1Value={addressLine1Value}
              addressLine2Value={addressLine2Value}
              appTitle={appTitle}
              cityValue={cityValue}
              countryValue={countryValue}
              dateOfBirthValue={dateOfBirthValue}
              hasPhoneAuthProviderEnabled={hasPhoneAuthProviderEnabled}
              layout2Column={layout2Column}
              missingAddressFields={hasMissingAddressFields}
              phoneNumberValue={phoneNumberValue}
              postalCodeValue={postalCodeValue}
              progressActivityNumber={progressActivityNumber}
              setAddressLine1Value={setAddressLine1Value}
              setAddressLine2Value={setAddressLine2Value}
              setCityValue={setCityValue}
              setCountryValue={setCountryValue}
              setDateOfBirthValue={setDateOfBirthValue}
              setPhoneNumberValue={setPhoneNumberValue}
              setPostalCodeValue={setPostalCodeValue}
              setStateValue={setStateValue}
              stateValue={stateValue}
            />
          </Show>

          <Show show={largeSaveButtonEnabled}>
            <KitButton
              color={clientBrandColor}
              disabled={!isEdited || isLoading}
              isLoading={isLoading}
              onPress={handleSave}
              style={{ marginBottom: Spacing.m }}
              title={t('account:save')}
              testID='button-save'
            />
          </Show>

          <Show show={includeLogoutButton}>
            <KitButton
              onPress={handleLogOut}
              secondary
              style={{ marginBottom: Spacing.m }}
              title={t('account:logout')}
              testID='button-logout'
            />
          </Show>

          <Show show={Boolean(deleteAccountAction)}>
            <KitButton
              onPress={() => setDeleteAccountModalVisible(true)}
              color={Colors.R500}
              style={{ marginBottom: Spacing.m }}
              title={t('account:deleteAccountTitle')}
              testID='button-delete'
            />
          </Show>

          <ChangeEmailModal
            appKey={appKey}
            clientBrandColor={clientBrandColor}
            email={email}
            profileID={profileID}
            setRefreshing={setRefreshing}
            setVisible={setEmailModalVisible}
            updateEmailAction={updateEmailAction}
            visible={emailModalVisible}
          />

          <ChangePhoneNumberModal
            appKey={appKey}
            clientBrandColor={clientBrandColor}
            phoneNumber={phoneNumber}
            phoneNumberObj={phoneNumberObj}
            verifiedPhoneNumber={verifiedPhoneNumber}
            profileID={profileID}
            setRefreshing={setRefreshing}
            setVisible={setPhoneNumberModalVisible}
            updatePhoneAction={updatePhoneAction}
            visible={phoneNumberModalVisible}
          />

          <ChangePasswordModal
            clientBrandColor={clientBrandColor}
            saveNewPassword={saveNewPassword}
            setVisible={setPasswordModalVisible}
            visible={passwordModalVisible}
          />

          <DeleteAccountModal
            appKey={appKey}
            setVisible={setDeleteAccountModalVisible}
            visible={deleteAccountModalVisible}
            deleteAccountAction={deleteAccountAction}
          />

          <RemovePhoneLoginModal
            appKey={appKey}
            setVisible={setRemovePhoneLoginModalVisible}
            visible={removePhoneLoginModalVisible}
            updatePhoneAction={updatePhoneAction}
            setRefreshing={setRefreshing}
            verifiedPhoneNumber={verifiedPhoneNumber}
            handleLogOut={handleLogOut}
            loggedInAuthProvider={loggedInAuthProvider}
          />
        </View>
      </KeyboardAwareScrollView>
    </View>
  );
};

export default AccountBlock;

const styles = StyleSheet.create({
  headerContainer: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: Spacing.m,
  },
  label: {
    color: Colors.N500,
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 18,
    marginBottom: Spacing.s,
  },
  errorLabel: {
    backgroundColor: Colors.R500,
    borderRadius: Spacing.s,
    marginTop: Spacing.s,
    paddingBottom: Spacing.s,
    paddingLeft: Spacing.l,
    paddingRight: Spacing.l,
    paddingTop: Spacing.s,
    position: 'absolute',
    top: 247,
    zIndex: 2,
  },
});
